/**
 * Open-Meteo client — keyless weather + geocoding API.
 *
 * Why Open-Meteo: rate-limits per IP (10k req/day), so every streamer's
 * machine has its own quota and Eriol pays/auths nothing. CORS-enabled,
 * so the renderer can call directly without a backend proxy.
 *
 * Attribution: docs require visible credit ("Powered by Open-Meteo") or
 * a commercial license. The config modal renders that credit line.
 *
 * Two endpoints we hit:
 *   - geocoding-api.open-meteo.com/v1/search  → city lookup
 *   - api.open-meteo.com/v1/forecast          → current + daily forecast
 */

const GEO_BASE = "https://geocoding-api.open-meteo.com/v1/search";
const FORECAST_BASE = "https://api.open-meteo.com/v1/forecast";

// ---------------------------------------------------------------------------
// Geocoding
// ---------------------------------------------------------------------------

export interface IGeocodeResult {
  id: number;
  name: string;
  /** Province / state. Sometimes absent for small towns. */
  admin1?: string;
  /** Country English name. */
  country?: string;
  country_code?: string;
  latitude: number;
  longitude: number;
  /** IANA timezone (e.g. `America/Argentina/Buenos_Aires`). */
  timezone?: string;
  population?: number;
}

interface IGeocodeResponse {
  results?: IGeocodeResult[];
  // `generationtime_ms` etc. — unused.
}

/**
 * Look up cities matching `query`. Returns up to `count` results
 * ordered by population (Open-Meteo's default ranking). Empty array
 * when the query has no matches or is shorter than 2 chars (the
 * geocoder requires that minimum and returns 400 otherwise).
 *
 * `language` controls the result names — pass the user's UI language
 * so "São Paulo" comes back as "São Paulo" in PT, "San Pablo" in ES.
 */
export const geocodeCity = async (
  query: string,
  language: string = "en",
  count: number = 5,
  signal?: AbortSignal,
): Promise<IGeocodeResult[]> => {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  const url =
    `${GEO_BASE}?name=${encodeURIComponent(trimmed)}` +
    `&count=${count}&language=${encodeURIComponent(language)}&format=json`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Geocoding failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as IGeocodeResponse;
  return body.results ?? [];
};

// ---------------------------------------------------------------------------
// Forecast
// ---------------------------------------------------------------------------

export interface IWeatherCurrent {
  /** °C or °F depending on `units` passed to `fetchWeather`. */
  temperature: number;
  /** Open-Meteo `weather_code` — WMO 4677 codes. See `WMO_CODE_META`. */
  weatherCode: number;
  /** km/h. */
  windSpeed: number;
  /** 1 = day, 0 = night. Drives sun-vs-moon icon choice. */
  isDay: 0 | 1;
}

export interface IWeatherDaily {
  /** ISO date `YYYY-MM-DD`. */
  date: string;
  tempMin: number;
  tempMax: number;
  weatherCode: number;
}

export interface IWeatherForecast {
  current: IWeatherCurrent;
  daily: IWeatherDaily[];
  units: "celsius" | "fahrenheit";
}

interface IForecastResponse {
  current?: {
    temperature_2m: number;
    weather_code: number;
    wind_speed_10m: number;
    is_day: 0 | 1;
  };
  daily?: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    weather_code: number[];
  };
}

/**
 * Fetch current + daily forecast for the given coordinates.
 *
 * `forecastDays` is clamped 1–7 by Open-Meteo. The default 3 covers
 * the typical widget use-case (today + 2 days).
 *
 * `units` controls the temperature unit AND the wind unit (Open-Meteo
 * couples them — we don't surface wind unit separately).
 */
export const fetchWeather = async (
  args: {
    latitude: number;
    longitude: number;
    timezone?: string;
    units?: "celsius" | "fahrenheit";
    forecastDays?: number;
  },
  signal?: AbortSignal,
): Promise<IWeatherForecast> => {
  const tz = args.timezone || "auto";
  const units = args.units || "celsius";
  const days = Math.min(7, Math.max(1, args.forecastDays ?? 3));
  const tempUnit = units === "fahrenheit" ? "fahrenheit" : "celsius";
  const windUnit = units === "fahrenheit" ? "mph" : "kmh";
  const params = new URLSearchParams({
    latitude: args.latitude.toFixed(4),
    longitude: args.longitude.toFixed(4),
    current: "temperature_2m,weather_code,wind_speed_10m,is_day",
    daily: "temperature_2m_max,temperature_2m_min,weather_code",
    timezone: tz,
    temperature_unit: tempUnit,
    wind_speed_unit: windUnit,
    forecast_days: String(days),
  });
  const url = `${FORECAST_BASE}?${params.toString()}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Weather fetch failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as IForecastResponse;
  if (!body.current || !body.daily) {
    throw new Error("Weather response missing current or daily payload");
  }
  return {
    units,
    current: {
      temperature: body.current.temperature_2m,
      weatherCode: body.current.weather_code,
      windSpeed: body.current.wind_speed_10m,
      isDay: body.current.is_day,
    },
    daily: body.daily.time.map((date, i) => ({
      date,
      tempMin: body.daily!.temperature_2m_min[i],
      tempMax: body.daily!.temperature_2m_max[i],
      weatherCode: body.daily!.weather_code[i],
    })),
  };
};

// ---------------------------------------------------------------------------
// WMO weather code → human label + icon hint
//
// The full WMO 4677 table has dozens of codes; Open-Meteo collapses to
// the subset below. We map each to:
//   - a short human label (rendered as the secondary line of the widget)
//   - an `iconKey` enum the runtime maps to a Lucide icon
// The icon set deliberately avoids cute / cartoon variants — the widget
// is meant to slot into pro overlays, not a kids app.
// ---------------------------------------------------------------------------

export type IWeatherIconKey =
  | "sun"
  | "moon"
  | "cloud-sun"
  | "cloud-moon"
  | "cloud"
  | "cloud-fog"
  | "cloud-drizzle"
  | "cloud-rain"
  | "cloud-snow"
  | "cloud-lightning"
  | "cloud-hail";

interface IWmoMeta {
  label: { en: string; es: string; pt: string };
  icon: IWeatherIconKey;
  /** Some codes only make sense at day or night — for those we let the
   *  runtime swap to the moon variant when `isDay = 0`. */
  daySwap?: { day: IWeatherIconKey; night: IWeatherIconKey };
}

const WMO_TABLE: Record<number, IWmoMeta> = {
  0: {
    label: { en: "Clear", es: "Despejado", pt: "Limpo" },
    icon: "sun",
    daySwap: { day: "sun", night: "moon" },
  },
  1: {
    label: {
      en: "Mostly clear",
      es: "Mayormente despejado",
      pt: "Predominantemente limpo",
    },
    icon: "cloud-sun",
    daySwap: { day: "cloud-sun", night: "cloud-moon" },
  },
  2: {
    label: {
      en: "Partly cloudy",
      es: "Parcialmente nublado",
      pt: "Parcialmente nublado",
    },
    icon: "cloud-sun",
    daySwap: { day: "cloud-sun", night: "cloud-moon" },
  },
  3: {
    label: { en: "Overcast", es: "Nublado", pt: "Encoberto" },
    icon: "cloud",
  },
  45: {
    label: { en: "Fog", es: "Niebla", pt: "Névoa" },
    icon: "cloud-fog",
  },
  48: {
    label: {
      en: "Rime fog",
      es: "Niebla con escarcha",
      pt: "Névoa congelante",
    },
    icon: "cloud-fog",
  },
  51: {
    label: { en: "Light drizzle", es: "Llovizna ligera", pt: "Chuvisco fraco" },
    icon: "cloud-drizzle",
  },
  53: {
    label: { en: "Drizzle", es: "Llovizna", pt: "Chuvisco" },
    icon: "cloud-drizzle",
  },
  55: {
    label: {
      en: "Heavy drizzle",
      es: "Llovizna intensa",
      pt: "Chuvisco intenso",
    },
    icon: "cloud-drizzle",
  },
  56: {
    label: {
      en: "Freezing drizzle",
      es: "Llovizna helada",
      pt: "Chuvisco congelante",
    },
    icon: "cloud-drizzle",
  },
  57: {
    label: {
      en: "Heavy freezing drizzle",
      es: "Llovizna helada intensa",
      pt: "Chuvisco congelante intenso",
    },
    icon: "cloud-drizzle",
  },
  61: {
    label: { en: "Light rain", es: "Lluvia ligera", pt: "Chuva fraca" },
    icon: "cloud-rain",
  },
  63: { label: { en: "Rain", es: "Lluvia", pt: "Chuva" }, icon: "cloud-rain" },
  65: {
    label: { en: "Heavy rain", es: "Lluvia intensa", pt: "Chuva intensa" },
    icon: "cloud-rain",
  },
  66: {
    label: { en: "Freezing rain", es: "Lluvia helada", pt: "Chuva congelante" },
    icon: "cloud-rain",
  },
  67: {
    label: {
      en: "Heavy freezing rain",
      es: "Lluvia helada intensa",
      pt: "Chuva congelante intensa",
    },
    icon: "cloud-rain",
  },
  71: {
    label: { en: "Light snow", es: "Nieve ligera", pt: "Neve fraca" },
    icon: "cloud-snow",
  },
  73: { label: { en: "Snow", es: "Nieve", pt: "Neve" }, icon: "cloud-snow" },
  75: {
    label: { en: "Heavy snow", es: "Nieve intensa", pt: "Neve intensa" },
    icon: "cloud-snow",
  },
  77: {
    label: { en: "Snow grains", es: "Granos de nieve", pt: "Grãos de neve" },
    icon: "cloud-snow",
  },
  80: {
    label: { en: "Showers", es: "Chubascos", pt: "Pancadas de chuva" },
    icon: "cloud-rain",
  },
  81: {
    label: {
      en: "Heavy showers",
      es: "Chubascos intensos",
      pt: "Pancadas intensas",
    },
    icon: "cloud-rain",
  },
  82: {
    label: {
      en: "Violent showers",
      es: "Chubascos violentos",
      pt: "Pancadas violentas",
    },
    icon: "cloud-rain",
  },
  85: {
    label: {
      en: "Snow showers",
      es: "Chubascos de nieve",
      pt: "Pancadas de neve",
    },
    icon: "cloud-snow",
  },
  86: {
    label: {
      en: "Heavy snow showers",
      es: "Chubascos de nieve intensos",
      pt: "Pancadas de neve intensas",
    },
    icon: "cloud-snow",
  },
  95: {
    label: { en: "Thunderstorm", es: "Tormenta", pt: "Tempestade" },
    icon: "cloud-lightning",
  },
  96: {
    label: {
      en: "Thunderstorm w/ hail",
      es: "Tormenta con granizo",
      pt: "Tempestade com granizo",
    },
    icon: "cloud-hail",
  },
  99: {
    label: {
      en: "Heavy thunderstorm w/ hail",
      es: "Tormenta intensa con granizo",
      pt: "Tempestade intensa com granizo",
    },
    icon: "cloud-hail",
  },
};

const FALLBACK_META: IWmoMeta = {
  label: { en: "Unknown", es: "Desconocido", pt: "Desconhecido" },
  icon: "cloud",
};

/** Look up the human label + icon hint for a WMO code, with day/night
 *  swap when applicable (and `isDay` is provided). */
export const describeWeather = (
  code: number,
  language: "en" | "es" | "pt",
  isDay: 0 | 1 = 1,
): { label: string; icon: IWeatherIconKey } => {
  const meta = WMO_TABLE[code] ?? FALLBACK_META;
  const icon =
    meta.daySwap && isDay === 0
      ? meta.daySwap.night
      : (meta.daySwap?.day ?? meta.icon);
  return { label: meta.label[language] ?? meta.label.en, icon };
};
